import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../../services/api';

interface HealthEntry {
  id: string;
  data_date: string;
  steps: number;
  sleep_hours: number;
  sleep_quality: string;
  heart_rate_avg: number;
  calories_burned: number;
  active_minutes: number;
  source: string;
}

interface Recommendation {
  type: string;
  priority: string;
  message: string;
  icon: string;
}

const HealthOverlay: React.FC = () => {
  const [healthData, setHealthData] = useState<HealthEntry[]>([]);
  const [summary, setSummary] = useState({ averageSteps: 0, averageSleep: 0, totalDays: 0 });
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [integrations, setIntegrations] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newEntry, setNewEntry] = useState({
    dataDate: new Date().toISOString().split('T')[0],
    steps: '',
    sleepHours: '',
    sleepQuality: 'good',
    activeMinutes: ''
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [healthRes, recRes, intRes] = await Promise.all([
        apiService.getHealthData(),
        apiService.getHealthRecommendations(),
        apiService.getHealthIntegrations()
      ]);
      if (healthRes.success) {
        setHealthData(healthRes.healthData || []);
        setSummary(healthRes.summary || {});
      }
      if (recRes.success) setRecommendations(recRes.recommendations || []);
      if (intRes.success) setIntegrations(intRes.integrations || {});
    } catch (error) {
      console.error('Failed to fetch health data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAddEntry = async () => {
    try {
      await apiService.addHealthData({
        dataDate: newEntry.dataDate,
        steps: newEntry.steps ? parseInt(newEntry.steps) : null,
        sleepHours: newEntry.sleepHours ? parseFloat(newEntry.sleepHours) : null,
        sleepQuality: newEntry.sleepQuality,
        activeMinutes: newEntry.activeMinutes ? parseInt(newEntry.activeMinutes) : null
      });
      setShowAddModal(false);
      setNewEntry({ dataDate: new Date().toISOString().split('T')[0], steps: '', sleepHours: '', sleepQuality: 'good', activeMinutes: '' });
      fetchData();
    } catch (error) {
      console.error('Failed to add health data:', error);
      alert('Failed to save health data');
    }
  };

  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center' }}>Loading health data...</div>;
  }

  return (
    <div style={{ padding: '24px' }} data-testid="health-overlay">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#1f2937' }}>Health Data Overlay</h2>
          <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: '0.9rem' }}>Track steps, sleep to optimize your schedule</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          style={{ padding: '10px 20px', backgroundColor: '#10b981', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer' }}
        >
          + Log Health Data
        </button>
      </div>

      {/* Recommendations */}
      {recommendations.length > 0 && (
        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ margin: '0 0 12px 0', fontSize: '1rem', fontWeight: 600, color: '#374151' }}>Scheduling Recommendations</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {recommendations.map((rec, idx) => (
              <div key={idx} style={{
                display: 'flex', alignItems: 'center', padding: '12px 16px',
                backgroundColor: rec.priority === 'high' ? '#fef2f2' : rec.priority === 'medium' ? '#fffbeb' : '#f0fdf4',
                borderRadius: '8px',
                border: `1px solid ${rec.priority === 'high' ? '#fecaca' : rec.priority === 'medium' ? '#fde68a' : '#bbf7d0'}`
              }}>
                <span style={{ fontSize: '1.5rem', marginRight: '12px' }}>{rec.icon}</span>
                <p style={{ margin: 0, fontSize: '0.9rem', color: '#374151' }}>{rec.message}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        <div style={{ padding: '20px', backgroundColor: '#eff6ff', borderRadius: '12px', textAlign: 'center', border: '1px solid #bfdbfe' }}>
          <p style={{ margin: 0, fontSize: '0.85rem', color: '#1d4ed8' }}>Avg Steps (7 days)</p>
          <p style={{ margin: '8px 0 0 0', fontSize: '2rem', fontWeight: 700, color: '#1e40af' }}>{summary.averageSteps.toLocaleString()}</p>
        </div>
        <div style={{ padding: '20px', backgroundColor: '#f0fdf4', borderRadius: '12px', textAlign: 'center', border: '1px solid #bbf7d0' }}>
          <p style={{ margin: 0, fontSize: '0.85rem', color: '#15803d' }}>Avg Sleep (7 days)</p>
          <p style={{ margin: '8px 0 0 0', fontSize: '2rem', fontWeight: 700, color: '#166534' }}>{summary.averageSleep}h</p>
        </div>
        <div style={{ padding: '20px', backgroundColor: '#fdf4ff', borderRadius: '12px', textAlign: 'center', border: '1px solid #f5d0fe' }}>
          <p style={{ margin: 0, fontSize: '0.85rem', color: '#a21caf' }}>Days Tracked</p>
          <p style={{ margin: '8px 0 0 0', fontSize: '2rem', fontWeight: 700, color: '#86198f' }}>{summary.totalDays}</p>
        </div>
      </div>

      {/* Integrations Status */}
      <div style={{ backgroundColor: '#f9fafb', borderRadius: '12px', padding: '20px', marginBottom: '24px' }}>
        <h3 style={{ margin: '0 0 16px 0', fontSize: '1rem', fontWeight: 600, color: '#374151' }}>Connected Services</h3>
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
          <div style={{
            padding: '16px 24px',
            backgroundColor: 'white',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <span style={{ fontSize: '1.5rem' }}>🏃</span>
            <div>
              <p style={{ margin: 0, fontWeight: 600, color: '#374151' }}>Google Fit</p>
              <p style={{ margin: 0, fontSize: '0.8rem', color: integrations.google_fit?.connected ? '#10b981' : '#6b7280' }}>
                {integrations.google_fit?.connected ? 'Connected' : 'Not connected'}
              </p>
            </div>
          </div>
          <div style={{
            padding: '16px 24px',
            backgroundColor: 'white',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            opacity: 0.6
          }}>
            <span style={{ fontSize: '1.5rem' }}>🍎</span>
            <div>
              <p style={{ margin: 0, fontWeight: 600, color: '#374151' }}>Apple Health</p>
              <p style={{ margin: 0, fontSize: '0.8rem', color: '#6b7280' }}>Requires iOS app</p>
            </div>
          </div>
        </div>
        <p style={{ margin: '16px 0 0 0', fontSize: '0.85rem', color: '#6b7280' }}>
          OAuth integration with Google Fit requires admin setup. Contact your administrator to enable automatic sync.
        </p>
      </div>

      {/* Health Log Table */}
      <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #e5e7eb' }}>
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: '#374151' }}>Recent Health Log</h3>
        </div>
        {healthData.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <p style={{ color: '#6b7280', marginBottom: '16px' }}>No health data logged yet</p>
            <button
              onClick={() => setShowAddModal(true)}
              style={{ padding: '10px 20px', backgroundColor: '#10b981', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
            >
              Log Your First Entry
            </button>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f9fafb' }}>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '0.85rem', color: '#6b7280', fontWeight: 600 }}>Date</th>
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '0.85rem', color: '#6b7280', fontWeight: 600 }}>Steps</th>
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '0.85rem', color: '#6b7280', fontWeight: 600 }}>Sleep</th>
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '0.85rem', color: '#6b7280', fontWeight: 600 }}>Quality</th>
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '0.85rem', color: '#6b7280', fontWeight: 600 }}>Active Min</th>
                </tr>
              </thead>
              <tbody>
                {healthData.map(entry => (
                  <tr key={entry.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '12px 16px', color: '#374151' }}>{new Date(entry.data_date).toLocaleDateString()}</td>
                    <td style={{ padding: '12px 16px', textAlign: 'center', color: '#374151' }}>{entry.steps?.toLocaleString() || '-'}</td>
                    <td style={{ padding: '12px 16px', textAlign: 'center', color: '#374151' }}>{entry.sleep_hours ? `${entry.sleep_hours}h` : '-'}</td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '12px',
                        fontSize: '0.75rem',
                        backgroundColor: entry.sleep_quality === 'good' ? '#dcfce7' : entry.sleep_quality === 'fair' ? '#fef3c7' : '#fee2e2',
                        color: entry.sleep_quality === 'good' ? '#166534' : entry.sleep_quality === 'fair' ? '#92400e' : '#991b1b'
                      }}>
                        {entry.sleep_quality || '-'}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center', color: '#374151' }}>{entry.active_minutes || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add Entry Modal */}
      {showAddModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', borderRadius: '16px', padding: '24px', width: '90%', maxWidth: '400px' }}>
            <h3 style={{ margin: '0 0 20px 0' }}>Log Health Data</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.85rem', color: '#374151', fontWeight: 500 }}>Date</label>
                <input type="date" value={newEntry.dataDate} onChange={(e) => setNewEntry({ ...newEntry, dataDate: e.target.value })} style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '8px', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.85rem', color: '#374151', fontWeight: 500 }}>Steps</label>
                <input type="number" value={newEntry.steps} onChange={(e) => setNewEntry({ ...newEntry, steps: e.target.value })} placeholder="e.g., 8000" style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '8px', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.85rem', color: '#374151', fontWeight: 500 }}>Sleep Hours</label>
                <input type="number" step="0.5" value={newEntry.sleepHours} onChange={(e) => setNewEntry({ ...newEntry, sleepHours: e.target.value })} placeholder="e.g., 7.5" style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '8px', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.85rem', color: '#374151', fontWeight: 500 }}>Sleep Quality</label>
                <select value={newEntry.sleepQuality} onChange={(e) => setNewEntry({ ...newEntry, sleepQuality: e.target.value })} style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '8px' }}>
                  <option value="good">Good</option>
                  <option value="fair">Fair</option>
                  <option value="poor">Poor</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.85rem', color: '#374151', fontWeight: 500 }}>Active Minutes</label>
                <input type="number" value={newEntry.activeMinutes} onChange={(e) => setNewEntry({ ...newEntry, activeMinutes: e.target.value })} placeholder="e.g., 45" style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '8px', boxSizing: 'border-box' }} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
              <button onClick={() => setShowAddModal(false)} style={{ flex: 1, padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', backgroundColor: 'white', cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleAddEntry} style={{ flex: 1, padding: '12px', border: 'none', borderRadius: '8px', backgroundColor: '#10b981', color: 'white', cursor: 'pointer', fontWeight: 600 }}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HealthOverlay;
